import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryMetrics } from '../../hooks/useQueryMetrics';
import './PerformanceMonitor.css';

// Task 4.4b: this used to read seven fields from
// canvasService.getPerformanceMetrics() (the enhanced API service's own
// tracker, since deleted). The owner decision recorded in the
// implementation plan kept three -- averageResponseTime, cacheHitRate,
// cacheSize -- and dropped enhancedApiCalls, legacyApiCalls,
// performanceImprovement, and circuitBreakerStatus along with the code
// producing them. The three survivors now come from useQueryMetrics,
// which reads TanStack Query's own cache/event stream.
const PerformanceMonitor = ({ isVisible = true }) => {
  const queryClient = useQueryClient();
  const metrics = useQueryMetrics();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isVisible) {
    return null;
  }

  // Every call site below passes a field of QueryMetricsSnapshot, all three
  // of which are declared `number` there -- so these annotations are checked
  // against their callers rather than assumed.
  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatPercentage = (value: number) => {
    return `${Math.round(value * 100)}%`;
  };

  const getPerformanceColor = (responseTime: number) => {
    if (responseTime < 500) return '#4CAF50'; // Green - Good
    if (responseTime < 1500) return '#FF9800'; // Orange - Moderate
    return '#f44336'; // Red - Poor
  };

  const getCacheColor = (hitRate: number) => {
    if (hitRate > 0.8) return '#4CAF50'; // Green - Excellent
    if (hitRate > 0.5) return '#FF9800'; // Orange - Good
    return '#f44336'; // Red - Poor
  };

  return (
    <div className={`performance-monitor ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div
        className="performance-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="performance-title">📊 API Performance</span>
        <div className="performance-summary">
          <span
            className="performance-indicator"
            style={{ color: getPerformanceColor(metrics.averageResponseTime) }}
          >
            {formatDuration(metrics.averageResponseTime)}
          </span>
          <span
            className="cache-indicator"
            style={{ color: getCacheColor(metrics.cacheHitRate) }}
          >
            {formatPercentage(metrics.cacheHitRate)} cache
          </span>
        </div>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
      </div>

      {isExpanded && (
        <div className="performance-details">
          <div className="performance-section">
            <h4>Response Times</h4>
            <div className="metric-row">
              <span>Average Response:</span>
              <span style={{ color: getPerformanceColor(metrics.averageResponseTime) }}>
                {formatDuration(metrics.averageResponseTime)}
              </span>
            </div>
          </div>

          <div className="performance-section">
            <h4>Caching</h4>
            <div className="metric-row">
              <span>Cache Hit Rate:</span>
              <span style={{ color: getCacheColor(metrics.cacheHitRate) }}>
                {formatPercentage(metrics.cacheHitRate)}
              </span>
            </div>
            <div className="metric-row">
              <span>Cache Size:</span>
              <span>{metrics.cacheSize} entries</span>
            </div>
          </div>

          <div className="performance-actions">
            <button
              className="clear-cache-btn"
              onClick={() => queryClient.clear()}
            >
              Clear Cache
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMonitor;
